import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Archive,
  ArchiveRestore,
  CreditCard,
  History,
  Loader2,
  Mail,
  Pencil,
  ShoppingCart,
  X,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { StatusBadge } from '@/components/ui/StatusBadge';
import {
  useActivityLog,
  useArchiveActivity,
  type ActivityCategory,
  type ActivityRow,
} from '@/hooks/admin/useActivityLog';

/**
 * /admin/activity — the master log: every order, payment, staff change, and
 * outbound communication, one row per entry in a real sortable table.
 *
 * Columns: select / Type / Description / Date / Person / Customer / Status /
 * archive. Every data column sorts (click the header); a select-all checkbox
 * + per-row checkboxes drive bulk Archive / Restore. Archiving soft-hides a
 * row (nothing in any source table changes); the Archived view restores.
 */

const FILTERS: Array<{ key: ActivityCategory | 'all'; label: string }> = [
  { key: 'all', label: 'Everything' },
  { key: 'order', label: 'Orders' },
  { key: 'payment', label: 'Payments' },
  { key: 'change', label: 'Changes' },
  { key: 'comm', label: 'Communications' },
];

const CATEGORY_META: Record<
  ActivityCategory,
  { label: string; icon: typeof History; className: string }
> = {
  order: { label: 'Order', icon: ShoppingCart, className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  payment: { label: 'Payment', icon: CreditCard, className: 'bg-sky-50 text-sky-700 border-sky-200' },
  change: { label: 'Change', icon: Pencil, className: 'bg-amber-50 text-amber-700 border-amber-200' },
  comm: { label: 'Email', icon: Mail, className: 'bg-violet-50 text-violet-700 border-violet-200' },
};

/** Bulk archive/unarchive is capped server-side at 200 keys per call. */
const BULK_CHUNK = 200;

function CategoryChip({ category }: { category: ActivityCategory }) {
  const meta = CATEGORY_META[category];
  const Icon = meta.icon;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap',
        meta.className,
      )}
    >
      <Icon size={10} />
      {meta.label}
    </span>
  );
}

/** Header checkbox with the indeterminate ("some selected") state. */
function SelectAllBox({
  total,
  selected,
  onToggle,
}: {
  total: number;
  selected: number;
  onToggle: (all: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = selected > 0 && selected < total;
  }, [selected, total]);
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={selected === total && total > 0 ? 'Deselect all rows' : 'Select all rows'}
      className="h-3.5 w-3.5 accent-brix-primary cursor-pointer align-middle"
      checked={total > 0 && selected === total}
      disabled={total === 0}
      onChange={(e) => onToggle(e.target.checked)}
    />
  );
}

export default function AdminActivity() {
  const [filter, setFilter] = useState<ActivityCategory | 'all'>('all');
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const query = useActivityLog({
    category: filter === 'all' ? undefined : filter,
    archived: showArchived,
  });
  const archiveMutation = useArchiveActivity();
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const pages = query.data?.pages ?? [];
  const rows = pages.flatMap((p) => p.rows);

  // Selection is per-view: switching filter or live/archived clears it so a
  // bulk action can never touch rows you aren't looking at.
  useEffect(() => setSelected(new Set()), [filter, showArchived]);

  // A time window can be entirely archived (or entirely live, in the
  // Archived view), leaving an empty page with more history behind it —
  // keep pulling so the table never dead-ends on a blank screen.
  useEffect(() => {
    const last = pages[pages.length - 1];
    if (last && last.rows.length === 0 && query.hasNextPage && !query.isFetchingNextPage) {
      void query.fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages.length, query.hasNextPage, query.isFetchingNextPage]);

  function toggleRow(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleAll(all: boolean) {
    setSelected(all ? new Set(rows.map((r) => r.key)) : new Set());
  }

  function runArchive(keys: string[], archive: boolean) {
    const action = archive ? ('archive' as const) : ('unarchive' as const);
    // The endpoint caps at 200 keys per call — chunk larger selections.
    const chunks: string[][] = [];
    for (let i = 0; i < keys.length; i += BULK_CHUNK) chunks.push(keys.slice(i, i + BULK_CHUNK));
    void (async () => {
      for (const chunk of chunks) {
        await archiveMutation.mutateAsync({ action, keys: chunk });
      }
      setSelected(new Set());
    })().catch(() => {
      /* mutation error state renders below */
    });
  }

  function archiveOne(row: ActivityRow) {
    setBusyKey(row.key);
    archiveMutation.mutate(
      { action: showArchived ? 'unarchive' : 'archive', keys: [row.key] },
      {
        onSuccess: () =>
          setSelected((prev) => {
            const next = new Set(prev);
            next.delete(row.key);
            return next;
          }),
        onSettled: () => setBusyKey(null),
      },
    );
  }

  const fmtDate = (ts: string) =>
    new Date(ts).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      ...(new Date(ts).getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
    });

  const columns: Column<ActivityRow>[] = useMemo(
    () => [
      {
        key: 'select',
        header: (
          <SelectAllBox total={rows.length} selected={selected.size} onToggle={toggleAll} />
        ),
        width: '36px',
        align: 'center',
        cell: (row) => (
          <input
            type="checkbox"
            aria-label={`Select ${row.summary}`}
            className="h-3.5 w-3.5 accent-brix-primary cursor-pointer align-middle"
            checked={selected.has(row.key)}
            onChange={() => toggleRow(row.key)}
          />
        ),
      },
      {
        key: 'type',
        header: 'Type',
        width: '110px',
        sortValue: (row) => row.category,
        cell: (row) => <CategoryChip category={row.category} />,
      },
      {
        key: 'description',
        header: 'Description',
        sortValue: (row) => row.summary.toLowerCase(),
        cell: (row) => (
          <span className="block max-w-[26rem] truncate" title={row.summary}>
            {row.summary}
          </span>
        ),
      },
      {
        key: 'date',
        header: 'Date',
        width: '150px',
        sortValue: (row) => row.ts,
        cell: (row) => (
          <span className="whitespace-nowrap text-xs text-brix-muted tabular-nums">
            {fmtDate(row.ts)}
          </span>
        ),
      },
      {
        key: 'person',
        header: 'Person',
        width: '170px',
        sortValue: (row) => row.actor.toLowerCase(),
        cell: (row) => (
          <span className="block max-w-[10rem] truncate text-xs" title={row.actor}>
            {row.actor}
          </span>
        ),
        hideOnMobile: true,
      },
      {
        key: 'customer',
        header: 'Customer',
        width: '180px',
        sortValue: (row) => (row.customer_name ?? '').toLowerCase(),
        cell: (row) =>
          row.customer_name && row.customer_id ? (
            <Link
              to={`/admin/customers/${row.customer_id}`}
              className="block max-w-[11rem] truncate text-xs text-brix-primary hover:underline"
              title={row.customer_name}
            >
              {row.customer_name}
            </Link>
          ) : (
            <span className="text-xs text-brix-muted">—</span>
          ),
        hideOnMobile: true,
      },
      {
        key: 'status',
        header: 'Status',
        width: '110px',
        sortValue: (row) => row.status ?? '',
        cell: (row) =>
          row.status ? (
            <StatusBadge
              tone={row.status === 'failed' || row.status === 'error' ? 'danger' : 'neutral'}
              size="sm"
            >
              {row.status}
            </StatusBadge>
          ) : (
            <span className="text-xs text-brix-muted">—</span>
          ),
        hideOnMobile: true,
      },
      {
        key: 'action',
        header: '',
        width: '48px',
        align: 'right',
        cell: (row) => (
          <Button
            variant="ghost"
            size="sm"
            className="text-brix-muted hover:text-brix-ink"
            disabled={archiveMutation.isPending && busyKey === row.key}
            title={showArchived ? 'Restore to the live feed' : 'Archive (hide — restorable)'}
            onClick={() => archiveOne(row)}
          >
            {archiveMutation.isPending && busyKey === row.key ? (
              <Loader2 size={12} className="animate-spin" />
            ) : showArchived ? (
              <ArchiveRestore size={12} />
            ) : (
              <Archive size={12} />
            )}
          </Button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, selected, showArchived, archiveMutation.isPending, busyKey],
  );

  return (
    <div className="px-6 py-6 space-y-4">
      <div>
        <h2 className="font-display text-lg font-semibold text-brix-ink flex items-center gap-2">
          <History size={16} className="text-brix-primary" />
          Activity
        </h2>
        <p className="text-sm text-brix-muted mt-0.5 max-w-2xl">
          The master log — every order, payment, account change, and outbound email, with who
          did it. Click a column header to sort; check rows to archive in bulk (nothing is
          deleted — restore any time from the Archived view).
        </p>
      </div>

      {/* Category pills + archived toggle */}
      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={cn(
              'px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all duration-200 cursor-pointer',
              filter === f.key
                ? 'bg-brix-primary text-white border-brix-primary shadow-card'
                : 'bg-brix-surface text-brix-ink border-brix-border hover:bg-brix-primary-tint',
            )}
          >
            {f.label}
          </button>
        ))}
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setShowArchived((v) => !v)}
          className={cn(
            'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all duration-200 cursor-pointer',
            showArchived
              ? 'bg-brix-ink text-white border-brix-ink shadow-card'
              : 'bg-brix-surface text-brix-muted border-brix-border hover:text-brix-ink hover:bg-brix-surface-alt',
          )}
          title={showArchived ? 'Back to the live feed' : 'Show archived entries'}
        >
          <Archive size={12} />
          Archived
        </button>
      </div>

      {showArchived && (
        <div className="rounded-lg border border-brix-border bg-brix-surface-alt/40 px-3.5 py-2 text-xs text-brix-muted">
          Viewing archived entries — hidden from the live feed, nothing deleted. Check rows (or
          use ⤴) to restore.
        </div>
      )}

      {/* Bulk action bar — appears when anything is checked */}
      {selected.size > 0 && (
        <div className="sticky top-0 z-20 rounded-lg border border-brix-primary/30 bg-brix-primary-tint/60 backdrop-blur-sm px-3.5 py-2 flex items-center gap-3 flex-wrap">
          <span className="text-xs font-semibold text-brix-ink">
            {selected.size} selected
          </span>
          <Button
            size="sm"
            variant="primary"
            disabled={archiveMutation.isPending}
            onClick={() => runArchive(Array.from(selected), !showArchived)}
          >
            {archiveMutation.isPending ? (
              <Loader2 size={12} className="animate-spin" />
            ) : showArchived ? (
              <ArchiveRestore size={12} />
            ) : (
              <Archive size={12} />
            )}
            {showArchived ? `Restore ${selected.size}` : `Archive ${selected.size}`}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={archiveMutation.isPending}
            onClick={() => setSelected(new Set())}
          >
            <X size={12} /> Clear selection
          </Button>
          {archiveMutation.error instanceof Error && (
            <span className="text-xs text-brix-warning">{archiveMutation.error.message}</span>
          )}
        </div>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.key}
        isLoading={query.isLoading}
        error={query.error instanceof Error ? query.error.message : null}
        empty={
          showArchived
            ? 'Nothing archived yet.'
            : 'Nothing logged yet for this filter.'
        }
        initialSort={{ key: 'date', dir: 'desc' }}
        caption="Activity log"
        mobileRow={(row) => (
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              aria-label={`Select ${row.summary}`}
              className="mt-1 h-3.5 w-3.5 accent-brix-primary"
              checked={selected.has(row.key)}
              onChange={() => toggleRow(row.key)}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <CategoryChip category={row.category} />
                <span className="text-[11px] text-brix-muted">{fmtDate(row.ts)}</span>
              </div>
              <div className="text-sm text-brix-ink truncate mt-0.5" title={row.summary}>
                {row.summary}
              </div>
              <div className="text-[11px] text-brix-muted flex items-center gap-2 flex-wrap">
                <span>by {row.actor}</span>
                {row.customer_name && row.customer_id && (
                  <Link
                    to={`/admin/customers/${row.customer_id}`}
                    className="text-brix-primary hover:underline"
                  >
                    {row.customer_name}
                  </Link>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0 text-brix-muted"
              disabled={archiveMutation.isPending && busyKey === row.key}
              onClick={() => archiveOne(row)}
            >
              {showArchived ? <ArchiveRestore size={12} /> : <Archive size={12} />}
            </Button>
          </div>
        )}
      />

      {query.hasNextPage && rows.length > 0 && (
        <div className="flex justify-center">
          <Button
            variant="secondary"
            size="sm"
            disabled={query.isFetchingNextPage}
            onClick={() => void query.fetchNextPage()}
          >
            {query.isFetchingNextPage ? (
              <Loader2 size={13} className="animate-spin" />
            ) : null}
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Single-line activity entry — still used by the per-customer Communications
 * history panel (Billing & comms tab), which keeps the compact feed look.
 */
export function ActivityLine({
  row,
  hideCustomer,
  timeOnly,
  action,
}: {
  row: ActivityRow;
  hideCustomer?: boolean;
  /** Render just the clock time — used when a day header carries the date. */
  timeOnly?: boolean;
  /** Optional trailing control (e.g. an archive button). */
  action?: React.ReactNode;
}) {
  const meta = CATEGORY_META[row.category];
  const Icon = meta.icon;
  const when = new Date(row.ts).toLocaleString(
    'en-US',
    timeOnly
      ? { hour: 'numeric', minute: '2-digit' }
      : { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' },
  );
  const failed = row.status === 'failed' || row.status === 'error';

  return (
    <div className="px-4 py-2.5 flex items-start gap-3 text-sm group">
      <span
        className={cn(
          'shrink-0 inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold mt-0.5',
          meta.className,
        )}
      >
        <Icon size={10} />
        {meta.label}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-brix-ink truncate" title={row.summary}>
          {row.summary}
        </div>
        <div className="text-[11px] text-brix-muted flex items-center gap-2 flex-wrap">
          <span>{when}</span>
          <span>
            by <span className="font-medium text-brix-ink/80">{row.actor}</span>
          </span>
          {!hideCustomer && row.customer_name && row.customer_id && (
            <Link
              to={`/admin/customers/${row.customer_id}`}
              className="text-brix-primary hover:underline"
            >
              {row.customer_name}
            </Link>
          )}
          {row.status && (
            <StatusBadge tone={failed ? 'danger' : 'neutral'} size="sm">
              {row.status}
            </StatusBadge>
          )}
        </div>
      </div>
      {action}
    </div>
  );
}
